import { useEffect, useState } from "react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { apiRelativePath } from "../../lib/fileOpen";
import { Pill } from "../hs/hsUtils";
import { TONE_CLASSES } from "../assets/assetUtils";
import { Star } from "@phosphor-icons/react";

export const err = (e, fb) => toast.error(formatApiError(e?.response?.data?.detail) || fb);
export const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");
export const fmtDT = (d) => (d ? new Date(d).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
export const gbp = (n) => (n == null || n === "" ? "—" : `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`);
export const DAY_LABEL = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
const BLUE = "text-blue-700 bg-blue-50 border-blue-200 dark:text-blue-400 dark:bg-blue-950/30 dark:border-blue-800";
const VIOLET = "text-violet-700 bg-violet-50 border-violet-200 dark:text-violet-300 dark:bg-violet-950/30 dark:border-violet-800";

export const REQ_STATUS = {
  open: ["Awaiting quote", TONE_CLASSES.soon], quoted: ["Quote received", BLUE], offered: ["Job offered", VIOLET], accepted: ["Agreed", TONE_CLASSES.ok],
  completed: ["Completed", TONE_CLASSES.ok], declined: ["Declined", TONE_CLASSES.overdue], withdrawn: ["Withdrawn", TONE_CLASSES.none],
};
export const LISTING_STATUS = {
  draft: ["Draft", TONE_CLASSES.none], pending_review: ["With Fixlr for review", TONE_CLASSES.soon], approved: ["Approved", TONE_CLASSES.ok],
  rejected: ["Changes needed", TONE_CLASSES.overdue], suspended: ["Suspended", TONE_CLASSES.overdue],
};

export const StatusPill = ({ map, value, testid }) => {
  const [label, tone] = map[value] || [value, TONE_CLASSES.none];
  return <Pill tone={tone} testid={testid}>{label}</Pill>;
};

export function Stars({ value = 0, count, size = 14, testid }) {
  return (
    <span className="inline-flex items-center gap-0.5" data-testid={testid} title={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((i) => <Star key={i} size={size} weight={i <= Math.round(value) ? "fill" : "regular"} className={i <= Math.round(value) ? "text-amber-500" : "text-muted-foreground/40"} />)}
      {count != null && <span className="text-xs text-muted-foreground ml-1">{count ? `${value} (${count})` : "No reviews yet"}</span>}
    </span>
  );
}

const cache = new Map();
/** Marketplace files need the bearer token, so <img src> can't be used directly. */
export function AuthImage({ url, alt = "", className = "", testid }) {
  const [src, setSrc] = useState(cache.get(url) || "");
  useEffect(() => {
    if (!url || cache.has(url)) { setSrc(cache.get(url) || ""); return; }
    let live = true;
    API.get(apiRelativePath(url), { responseType: "blob" }).then((r) => { const u = URL.createObjectURL(r.data); cache.set(url, u); if (live) setSrc(u); }).catch(() => {});
    return () => { live = false; };
  }, [url]);
  if (!src) return <div className={`bg-muted animate-pulse ${className}`} data-testid={testid} />;
  return <img src={src} alt={alt} className={className} data-testid={testid} />;
}

export const areaText = (a = {}) => a.nationwide ? "Nationwide" : [a.regions?.length ? (a.regions.length > 4 ? `${a.regions.slice(0, 4).join(", ")} +${a.regions.length - 4} more` : a.regions.join(", ")) : "", a.districts?.length ? a.districts.join(", ") : "", a.base_postcode && a.radius_miles ? `${a.radius_miles} mi of ${a.base_postcode}` : ""].filter(Boolean).join(" · ") || "Not set";
export const availText = (av = {}) => [av.days?.length ? av.days.map((d) => DAY_LABEL[d]).join(" ") : "", av.hours_from && av.hours_to ? `${av.hours_from}–${av.hours_to}` : "", av.emergency_callouts ? "24/7 emergencies" : ""].filter(Boolean).join(" · ") || "Not set";
export const rateText = (r = {}) => [r.hourly != null ? `${gbp(r.hourly)}/hr` : "", r.callout != null ? `${gbp(r.callout)} call-out` : "", r.day != null ? `${gbp(r.day)}/day` : ""].filter(Boolean).join(" · ") || "Rates on request";

export function Field({ label, children, hint, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <p className="text-[11px] text-muted-foreground mt-1">{hint}</p>}
    </label>
  );
}

export function Tile({ label, value, sub, testid, tone = "" }) {
  return (
    <div className={`rounded-lg border border-border bg-card px-4 py-3 ${tone}`} data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-xl font-bold font-[Chivo] tracking-tight mt-0.5">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}
